import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { Globe2, Link2, Loader2, Megaphone, MessageSquare, Pin, Send, Users, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Notice } from "@/components/common/States";
import { ADMIN_ROLES, useAuth } from "@/context/AuthContext";
import { api, errorMessage } from "@/lib/api";
import { LinkCard } from "./LinkCard";

const MAKS_ISI = 2000;
const MAKS_TAUTAN = 3;

function InisialAvatar({ nama, size = "md" }) {
  const initials = (nama || "S").split(" ").filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase();
  const sizeClass = size === "lg" ? "h-11 w-11 text-sm" : "h-9 w-9 text-xs";
  return (
    <span className={`inline-flex ${sizeClass} shrink-0 items-center justify-center rounded-full bg-[#0B3D91] font-bold text-white dark:bg-[#3B82F6]`} aria-hidden>
      {initials}
    </span>
  );
}

export function Composer({ ctx, onPublished }) {
  const { me } = useAuth();
  const pengurus = ADMIN_ROLES.includes(ctx.role);
  const [open, setOpen] = useState(false);
  const [body, setBody] = useState("");
  const [audience, setAudience] = useState("all");
  const [classIds, setClassIds] = useState([]);
  const [links, setLinks] = useState([]);
  const [linkInput, setLinkInput] = useState("");
  const [showLink, setShowLink] = useState(false);
  const [pinned, setPinned] = useState(false);
  const [publik, setPublik] = useState(false);
  const [komentar, setKomentar] = useState(true);
  const [err, setErr] = useState("");

  const pratinjau = useMutation({
    mutationFn: async (url) => (await api.post("/school/feed/link-preview", { url })).data,
    onSuccess: (d) => {
      setLinks((l) => (l.some((x) => x.url === d.url) ? l : [...l, d]));
      setLinkInput("");
      if (d.status !== "ok") toast.warning("Pratinjau tautan tidak lengkap. Tautan tetap bisa dibuka dari kartu.");
    },
    onError: (e) => setErr(errorMessage(e)),
  });

  const terbit = useMutation({
    mutationFn: async () => (await api.post("/school/feed/posts", {
      body: body.trim(),
      links: links.map((l) => ({ url: l.url })),
      audience,
      class_ids: audience === "class" ? classIds : [],
      pinned: audience === "all" && pinned,
      public: audience === "all" && publik,
      comments_enabled: komentar,
    })).data,
    onSuccess: (post) => {
      setBody(""); setLinks([]); setClassIds([]); setPinned(false); setPublik(false);
      setKomentar(true); setErr(""); setAudience("all"); setShowLink(false);
      toast.success(post.kind === "pengumuman" ? "Pengumuman diterbitkan." : "Postingan diterbitkan.");
      onPublished?.(post);
      setOpen(false);
    },
    onError: (e) => setErr(errorMessage(e)),
  });

  const siap = body.trim().length > 0 && (audience === "all" || classIds.length > 0);
  const gantiKelas = (id, on) => setClassIds((c) => (on ? [...c, id] : c.filter((x) => x !== id)));

  const audienceLabel = audience === "all"
    ? (publik ? "Publik" : "Seluruh sekolah")
    : `Kelas (${classIds.length})`;

  return (
    <>
      {/* Compact trigger bar — like Facebook "What's on your mind?" */}
      <section className="rounded-xl border bg-card p-2.5 sm:p-3" data-testid="feed-composer">
        <div className="flex items-center gap-2.5 sm:gap-3">
          <InisialAvatar nama={me?.user.full_name} />
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="flex-1 rounded-full bg-muted px-3.5 sm:px-4 py-2 sm:py-2.5 text-left text-sm sm:text-[15px] text-muted-foreground transition-colors hover:bg-muted/80"
            data-testid="composer-trigger"
          >
            Mau posting apa hari ini, {me?.user.full_name?.split(" ")[0] || "Anda"}?
          </button>
        </div>

        <Separator className="my-2 sm:my-2.5" />

        <div className="flex items-center" data-testid="composer-quick-actions">
          <button
            type="button"
            onClick={() => { setOpen(true); setShowLink(true); }}
            className="flex flex-1 items-center justify-center gap-1 sm:gap-1.5 py-1.5 text-xs sm:text-sm font-medium text-muted-foreground transition-colors hover:bg-muted rounded-md"
            data-testid="composer-quick-link"
          >
            <Link2 className="h-4 w-4 text-[#3B82F6]" />
            <span className="hidden sm:inline">Tautan</span>
          </button>
          <button
            type="button"
            onClick={() => { setOpen(true); setAudience("class"); }}
            className="flex flex-1 items-center justify-center gap-1 sm:gap-1.5 py-1.5 text-xs sm:text-sm font-medium text-muted-foreground transition-colors hover:bg-muted rounded-md"
            data-testid="composer-quick-class"
          >
            <Users className="h-4 w-4 text-[#10B981]" />
            <span className="hidden sm:inline">Kelas</span>
          </button>
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="flex flex-1 items-center justify-center gap-1 sm:gap-1.5 py-1.5 text-xs sm:text-sm font-medium text-muted-foreground transition-colors hover:bg-muted rounded-md"
            data-testid="composer-quick-more"
          >
            <Megaphone className="h-4 w-4 text-[#F59E0B]" />
            <span className="hidden sm:inline">Pengumuman</span>
          </button>
        </div>
      </section>

      {/* Full composer modal */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl gap-0 p-0 sm:max-w-[540px]">
          <DialogHeader className="border-b px-5 py-4">
            <DialogTitle className="text-center text-lg font-bold">Buat postingan</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 px-5 py-4">
            {/* Author row */}
            <div className="flex items-center gap-3">
              <InisialAvatar nama={me?.user.full_name} size="lg" />
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold">{me?.user.full_name || "Anda"}</p>
                <button
                  type="button"
                  onClick={() => setAudience(audience === "all" ? "class" : "all")}
                  className="mt-0.5 inline-flex items-center gap-1 rounded-md bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted/80"
                  data-testid="composer-audience-badge"
                >
                  {audience === "all" ? <Globe2 className="h-3 w-3" /> : <Users className="h-3 w-3" />}
                  {audienceLabel}
                </button>
              </div>
            </div>

            {/* Textarea */}
            <Textarea
              value={body}
              onChange={(e) => setBody(e.target.value.slice(0, MAKS_ISI))}
              rows={5}
              placeholder={pengurus ? "Tulis pengumuman untuk sekolah…" : "Bagikan informasi untuk kelas Anda…"}
              className="min-h-[120px] resize-none border-0 px-0 text-base shadow-none focus-visible:ring-0"
              aria-label="Isi postingan"
              data-testid="composer-body"
              autoFocus
            />

            {/* Character counter */}
            <p className="text-right text-xs tabular-nums text-muted-foreground" data-testid="composer-counter">
              {body.length}/{MAKS_ISI}
            </p>

            {/* Link previews */}
            {links.length > 0 && (
              <ul className="grid gap-2" data-testid="composer-links">
                {links.map((l) => (
                  <li key={l.url} className="relative">
                    <LinkCard link={l} />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      aria-label={`Hapus tautan ${l.url}`}
                      className="absolute right-1 top-1 h-7 w-7 bg-background/90"
                      data-testid="composer-link-remove"
                      onClick={() => setLinks((x) => x.filter((y) => y.url !== l.url))}
                    >
                      <X className="h-3.5 w-3.5" />
                    </Button>
                  </li>
                ))}
              </ul>
            )}

            {/* Link input */}
            {showLink && links.length < MAKS_TAUTAN && (
              <div className="flex gap-2" data-testid="composer-link-form">
                <Input
                  value={linkInput}
                  onChange={(e) => setLinkInput(e.target.value)}
                  type="url"
                  inputMode="url"
                  placeholder="https://youtube.com/watch?v=…"
                  aria-label="Alamat tautan"
                  data-testid="composer-link-input"
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); pratinjau.mutate(linkInput.trim()); } }}
                />
                <Button
                  type="button"
                  variant="outline"
                  disabled={!linkInput.trim() || pratinjau.isPending}
                  onClick={() => pratinjau.mutate(linkInput.trim())}
                  data-testid="composer-link-add"
                >
                  {pratinjau.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Link2 className="h-4 w-4" />}
                </Button>
              </div>
            )}

            {/* Class picker */}
            {audience === "class" && (
              <div className="rounded-xl border p-3" data-testid="composer-class-picker">
                <p className="mb-2 text-xs font-semibold text-muted-foreground">Pilih kelas sasaran</p>
                {ctx.classes.length ? (
                  <ul className="grid max-h-40 gap-1.5 overflow-y-auto sm:grid-cols-2">
                    {ctx.classes.map((k) => (
                      <li key={k.id}>
                        <label className="flex cursor-pointer items-center gap-2 rounded-lg border px-2.5 py-2 text-sm hover:bg-muted" data-testid={`composer-class-${k.name}`}>
                          <Checkbox checked={classIds.includes(k.id)} onCheckedChange={(v) => gantiKelas(k.id, v === true)} />
                          <span className="min-w-0 flex-1 truncate">{k.name}</span>
                          <span className="text-xs text-muted-foreground">{k.student_count}</span>
                        </label>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-muted-foreground">Anda belum ditugaskan di kelas mana pun.</p>
                )}
              </div>
            )}

            {err && <Notice tone="danger" testId="composer-error">{err}</Notice>}
          </div>

          <Separator />

          {/* Settings row */}
          <div className="space-y-3 px-5 py-4">
            <p className="text-xs font-semibold text-muted-foreground">Pengaturan postingan</p>
            <div className="space-y-2.5">
              <label className="flex items-center justify-between rounded-lg px-1 py-1">
                <span className="flex items-center gap-2 text-sm">
                  <MessageSquare className="h-4 w-4 text-muted-foreground" /> Izinkan komentar
                </span>
                <Switch checked={komentar} onCheckedChange={setKomentar} data-testid="composer-comments" />
              </label>
              {audience === "all" && pengurus && (
                <label className="flex items-center justify-between rounded-lg px-1 py-1">
                  <span className="flex items-center gap-2 text-sm">
                    <Pin className="h-4 w-4 text-muted-foreground" /> Sematkan di atas
                  </span>
                  <Switch checked={pinned} onCheckedChange={setPinned} data-testid="composer-pinned" />
                </label>
              )}
              {audience === "all" && ctx.settings.public_page_enabled && (
                <label className="flex items-center justify-between rounded-lg px-1 py-1">
                  <span className="flex items-center gap-2 text-sm">
                    <Globe2 className="h-4 w-4 text-muted-foreground" /> Tampilkan di halaman publik
                  </span>
                  <Switch checked={publik} onCheckedChange={setPublik} data-testid="composer-public" />
                </label>
              )}
            </div>
          </div>

          <Separator />

          {/* Submit */}
          <div className="px-5 py-4">
            <Button
              className="w-full rounded-full py-2.5 text-sm font-semibold"
              disabled={!siap || terbit.isPending}
              onClick={() => { setErr(""); terbit.mutate(); }}
              data-testid="composer-submit"
            >
              {terbit.isPending ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Send className="mr-1.5 h-4 w-4" />}
              Terbitkan
            </Button>
            <p className="mt-2 text-center text-[11px] text-muted-foreground">
              Postingan dilihat warga sekolah sesuai sasaran. Jangan memuat nilai, nomor identitas, atau data pribadi siswa.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
